import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { useProjectStore } from '../project/project-store';
import type { RunsheetEntry } from '../types/api';

export const entriesKeys = {
  all: ['runsheet-entries'] as const,
  forRun: (projectId: string | null, runId: string) =>
    [...entriesKeys.all, 'forRun', projectId, runId] as const,
};

export function useEntriesForRun(titleRunId: string | null) {
  const projectId = useProjectStore((s) => s.projectId);
  return useQuery({
    queryKey: titleRunId ? entriesKeys.forRun(projectId, titleRunId) : ['entries', 'none'],
    enabled: !!titleRunId && !!projectId,
    queryFn: async () => {
      const res = await api.get<{ entries: RunsheetEntry[] }>('/runsheet-entries', {
        title_run_id: titleRunId ?? undefined,
        limit: 500,
      });
      return res.entries;
    },
  });
}

export interface EntryInput {
  title_run_id: string;
  document_type_id?: string | null | undefined;
  prospect_id?: string | null | undefined;
  instrument_date?: string | null | undefined;
  recording_date?: string | null | undefined;
  book?: string | null | undefined;
  page?: string | null | undefined;
  instrument_number?: string | null | undefined;
  legal_description?: string | null | undefined;
  consideration?: string | null | undefined;
  interest_conveyed?: string | null | undefined;
  depth_limitation?: string | null | undefined;
  area_limitation?: string | null | undefined;
  reservations?: string | null | undefined;
  township?: string | null | undefined;
  range?: string | null | undefined;
  section?: string | null | undefined;
  block?: string | null | undefined;
  abstract?: string | null | undefined;
  survey?: string | null | undefined;
  has_mineral_reservation?: boolean | undefined;
  mineral_reservation_description?: string | null | undefined;
  remarks?: string | null | undefined;
  reviewer_notes?: string | null | undefined;
}

export function useCreateEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EntryInput) =>
      api.post<{ entry: RunsheetEntry }>('/runsheet-entries', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: entriesKeys.all }),
  });
}

export function useUpdateEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Omit<EntryInput, 'title_run_id'>> }) =>
      api.patch<{ entry: RunsheetEntry }>(`/runsheet-entries/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: entriesKeys.all }),
  });
}

export function useDeleteEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/runsheet-entries/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: entriesKeys.all }),
  });
}

export function useReorderEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, after_id }: { id: string; after_id: string | null }) =>
      api.post<{ entry: RunsheetEntry }>(`/runsheet-entries/${id}/reorder`, { after_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: entriesKeys.all }),
  });
}
